<template>
  <v-dialog
    v-model="dialog"
    persistent
    scrollable
    :fullscreen="$vuetify.display.xs"
    :width="$vuetify.display.smAndUp ? 600 : 'auto'"
    style="z-index: 1100 !important;"
  >
    <template v-slot:activator="{ props }">
      <div v-bind="props">
        <slot></slot>
      </div>
    </template>

    <v-form ref="form" data-cy="dialog-convertImage-modal">
      <v-card>
        <v-card-title class="d-flex justify-space-between"
                      data-cy="dialog-convertImage-title">
          {{ $t('routes.pod.downloadImageAsFormat') }}

          <v-btn icon @click="dialog = false" :aria-label="$t('common.close')">
            <v-icon>mdi-close</v-icon>
          </v-btn>
        </v-card-title>

        <v-card-text scrollable class="pt-4 scrollable">
          <div data-cy="podMenu-tools-convertImage-selectFormat">
            <v-select
              v-model="selectedFormat"
              :items="imageFormats"
              :label="$t('routes.pod.menuMediaConverter.selectFormat')"
              :rules="selectedFormatRules"
              :menu-props="{ maxHeight: 320 }"
              :item-props="() => ({ tabindex: 0 })"
              variant="solo-filled"
              flat
              required
              single-line
            />
          </div>

          <div data-cy="podMenu-tools-convertImage-selectQuality">
            <v-select
              v-model="selectedQuality"
              :items="imageQuality"
              :label="$t('routes.pod.menuMediaConverter.selectQuality')"
              :rules="selectedQualityRules"
              item-title="name"
              item-value="value"
              :menu-props="{ maxHeight: 320 }"
              :item-props="() => ({ tabindex: 0 })"
              variant="solo-filled"
              flat
              required
              single-line
            />
          </div>

          <div v-show="successMessage" class="bg-success" data-cy="convertFile-successMessage">
            {{ successMessage }}
          </div>
          <div v-show="errorMessage" class="bg-error" data-cy="convertFile-errorMessage">
            {{ errorMessage }}
          </div>
        </v-card-text>

        <v-card-actions>
          <v-spacer></v-spacer>
          <v-btn
            class="bg-primary"
            @click="runJobs"
            elevation=0
            v-bind:disabled="isRunJobsDisabled"
            data-cy="dialog-convertFile-ok"
          >
            {{ $t('routes.pod.download') }}
          </v-btn>
          <v-btn variant="text" @click="dialog = false" data-cy="dialog-convertFile-false">
            {{ closeButtonLabel }}
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-form>

  </v-dialog>
</template>

<script>
import DialogActionKeys from '@/components/mixin/DialogActionKeys.js';
import DialogModel from '@/components/mixin/DialogModel.js';
import { usePodStore } from '@/stores/PodStore.js';

export default {
  mixins: [ DialogActionKeys, DialogModel ],
  name: 'DownloadImageAsDialog',
  props: {
    pod: {
      type: Object,
      required: true,
    },
  },
  data: function () {
    return {
      selectedFormat: 'png',
      selectedQuality: 0,

      closeButtonLabel: this.$t('common.cancel'),

      errorMessage: '',
      successMessage: '',
      preventDoubleClick: false,

      selectedFormatRules: [
        v => !!v || this.$t('routes.pod.menuMediaConverter.errorSelectedFileFormat'),
      ],
      selectedQualityRules: [
        v => v >= 0 || this.$t('routes.pod.menuMediaConverter.errorSelectedQuality'),
      ],
    };
  },
  watch: {
    dialog(newValue) {
      if (newValue) {
        this.resetForm();
      }
    },
  },
  methods: {
    resetForm() {
      if (this.$refs.form) {
        this.$refs.form.reset();
      }

      this.$nextTick(() => {
        this.errorMessage = '';
        this.successMessage = '';
        this.preventDoubleClick = false;

        this.selectedFormat = 'png';
        this.selectedQuality = 0;

        this.closeButtonLabel = this.$t('common.cancel');
      });
    },
    runJobs: async function () {
      const validForm = await this.$refs.form.validate();
      if (!validForm.valid) {
        return;
      }

      this.errorMessage = '';
      this.successMessage = '';

      this.preventDoubleClick = true;

      setTimeout(() => {
        this.preventDoubleClick = false;
      }, 3000);

      const podStore = usePodStore();

      try {
        await podStore.convertImage({
          id: this.pod.id,
          format: this.selectedFormat,
          quality: this.selectedQuality,
        });

        this.successMessage = this.$t('routes.pod.downloadImageProcessing');

        setTimeout(() => {
          this.successMessage = '';
        }, 10000);

        this.closeButtonLabel = this.$t('common.close');
      } catch (error) {
        console.log(error);

        if (error && error.message) {
          this.errorMessage = this.$localizeMessage(error.message);
        } else {
          this.errorMessage = this.$t('backend.system-error');
        }

        this.preventDoubleClick = false;
      }
    },
  },
  computed: {
    isRunJobsDisabled: function () {
      return this.preventDoubleClick === true;
    },
    imageFormats: function () {
      return [ 'jpg', 'png', 'bmp', 'pdf', 'webp', 'tiff', 'heic', 'gif' ];
    },
    imageQuality: function () {
      return [
        {
          name: this.$t('routes.pod.menuMediaConverter.imageLowest'),
          value: 100,
        }, {
          name: this.$t('routes.pod.menuMediaConverter.imageLow'),
          value: 75,
        },
        {
          name: this.$t('routes.pod.menuMediaConverter.imageAverage'),
          value: 50,
        },
        {
          name: this.$t('routes.pod.menuMediaConverter.imageGood'),
          value: 25,
        },
        {
          name: this.$t('routes.pod.menuMediaConverter.imageBest'),
          value: 0,
        },
      ];
    },
  },
};
</script>

<style>

</style>
